import type { ReactNode } from 'react'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Typography from '@mui/material/Typography'
import { Link as RouterLink } from 'react-router'

import { CodeBlock } from '../../components/ui/CodeBlock'
import { PageHeader } from '../../components/ui/PageHeader'
import { ENVIRONMENTS } from '../services/environments'
import { Ltr } from '../../components/ui/Ltr'
import {
  ASYNC_EXAMPLE,
  ERRORS,
  ERROR_EXAMPLE,
  IDEMPOTENCY_EXAMPLE,
  INSTALL,
  SYNC_EXAMPLE,
} from './content'
import Link from '@mui/material/Link'

/**
 * The one page a developer reads before writing any code.
 *
 * It answers the questions every integration asks, in the order they are
 * asked: what do I create first, how do I authenticate, what am I allowed to
 * call, where do I send it, who pays, what happens when I retry, what do the
 * failures mean, and where do I see what I did. The per-service contracts live
 * on each service's own page; this is what is true of every call.
 */
const SECTIONS = [
  { id: 'section-1', title: '۱. مسیر شروع' },
  { id: 'section-2', title: '۲. اپلیکیشن‌ها' },
  { id: 'section-3', title: '۳. احراز هویت' },
  { id: 'section-4', title: '۴. اعتبارنامه‌ها' },
  { id: 'section-5', title: '۵. دسترسی سرویس‌ها' },
  { id: 'section-6', title: '۶. پایهٔ نشانی و محیط‌ها' },
  { id: 'section-7', title: '۷. کیف پول پیش‌پرداخت' },
  { id: 'section-8', title: '۸. کلید یکتایی و شناسه‌ها' },
  { id: 'section-9', title: '۹. خطاهای عمومی' },
  { id: 'section-10', title: '۱۰. تاریخچهٔ درخواست‌ها' },
  { id: 'section-11', title: '۱۱. کتابخانهٔ Python' },
]

export function DocsPage() {
  return (
    <Box>
      <PageHeader
        title="مستندات فنی"
        description="هرچه برای اتصال سامانهٔ خودتان به این پلتفرم لازم دارید: احراز هویت، دسترسی‌ها، پرداخت، کلید یکتایی و معنی خطاها."
        action={
          <Button component={RouterLink} to="/services" size="small">
            فهرست سرویس‌ها
          </Button>
        }
      />

      <Contents sections={SECTIONS} />

      <Section id="section-1" title="۱. مسیر شروع">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هفت قدم، به همین ترتیب. هر قدم در بخش‌های بعدی همین صفحه توضیح داده
          شده است.
        </Typography>
        <Box
          component="ol"
          aria-label="قدم‌های شروع"
          sx={{ pr: 3, m: 0, '& li': { mb: 1 } }}
        >
          <Typography component="li" variant="body2" color="text.secondary">
            یک <RouterLink to="/applications">اپلیکیشن</RouterLink> بسازید — برای
            هر یکپارچه‌سازی یکی.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            منتظر <RouterLink to="/services">دسترسی سرویس‌ها</RouterLink> بمانید؛
            این قدم از سمت ما انجام می‌شود.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            یک <RouterLink to="/credentials">اعتبارنامه</RouterLink> بسازید و رمز
            آن را همان لحظه ذخیره کنید.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            پایهٔ نشانی محیط آزمون را در پیکربندی خود بگذارید (بخش ۶).
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            نخستین فراخوانی را انجام دهید — یا از کد خودتان، یا از
            «جزئیات و آزمایش» در صفحهٔ همان سرویس.
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            کلید یکتایی و مدیریت خطا را پیاده کنید (بخش‌های ۸ و ۹).
          </Typography>
          <Typography component="li" variant="body2" color="text.secondary">
            نتیجه را در <RouterLink to="/requests">مصرف و درخواست‌ها</RouterLink>
            {' '}ببینید.
          </Typography>
        </Box>
      </Section>

      <Section id="section-2" title="۲. اپلیکیشن‌ها">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هر چیز دیگری به یک اپلیکیشن وصل است: دسترسی سرویس‌ها، اعتبارنامه‌ها و
          تاریخچهٔ درخواست‌ها همگی به یک اپلیکیشن تعلق دارند. بنابراین نخستین کار،
          ساخت اپلیکیشن است.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          برای هر یکپارچه‌سازی یک اپلیکیشن جداگانه بسازید. اگر روزی اعتبارنامه‌ای
          لو رفت، ابطال آن تنها همان یکپارچه‌سازی را متوقف می‌کند، نه همهٔ ترافیک
          شما.
        </Typography>
      </Section>

      <Section id="section-3" title="۳. احراز هویت">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هر اپلیکیشن شما یک شناسهٔ کارخواه و یک رمز دارد. این دو را با یکدیگر به
          سامانه می‌فرستید و در پاسخ یک توکن دسترسی کوتاه‌عمر می‌گیرید؛ سپس آن
          توکن را در سرآیند مجوز هر فراخوانی می‌گذارید. کتابخانهٔ Python این کار
          را خودش انجام می‌دهد و توکن را تا نزدیک انقضا نگه می‌دارد.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          توکن برای شما مبهم است و چیزی درون آن برای رمزگشایی وجود ندارد. اعتبار
          آن در هر فراخوانی در برابر اعتبارنامه بررسی می‌شود؛ به همین دلیل ابطال
          یک اعتبارنامهٔ لو رفته بی‌درنگ اثر می‌گذارد.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          این اعتبارنامه‌ها ماشین را احراز هویت می‌کنند، نه شخص را. آن‌ها را در
          مخزن کد یا گزارش‌ها نگذارید.
        </Typography>
      </Section>

      <Section id="section-4" title="۴. اعتبارنامه‌ها">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          اعتبارنامه را در بخش{' '}
          <RouterLink to="/credentials">اعتبارنامه‌ها</RouterLink> می‌سازید. رمز
          تنها یک‌بار، در همان لحظهٔ ساخت، نمایش داده می‌شود و پس از بستن آن پنجره
          دیگر از هیچ راهی قابل بازیابی نیست؛ اگر گمش کردید، اعتبارنامهٔ تازه‌ای
          بسازید.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          می‌توانید هم‌زمان بیش از یک اعتبارنامهٔ فعال داشته باشید. برای تعویض
          بدون قطعی: نخست اعتبارنامهٔ جایگزین را بسازید، آن را مستقر کنید و سپس
          اعتبارنامهٔ پیشین را باطل کنید.
        </Typography>
      </Section>

      <Section id="section-5" title="۵. دسترسی سرویس‌ها">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هر اپلیکیشن تنها سرویس‌هایی را می‌تواند فراخوانی کند که برایش دسترسی داده
          شده باشد. این دسترسی از سمت ما داده می‌شود و از داخل پرتال قابل
          خوددهی نیست؛ فهرست سرویس‌های در دسترس شما در{' '}
          <RouterLink to="/services">دسترسی سرویس‌ها</RouterLink> آمده است.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          فراخوانی سرویسی که به آن دسترسی ندارید با کد
          <Ltr component="code" sx={{ mx: 0.5 }}>
            permission_denied
          </Ltr>
          رد می‌شود و هزینه‌ای هم ندارد.
        </Typography>
      </Section>

      <Section id="section-6" title="۶. پایهٔ نشانی و محیط‌ها">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          همهٔ فراخوانی‌ها روی یکی از نشانی‌های زیر انجام می‌شود و مسیرها زیر
          <Ltr component="code" sx={{ mx: 0.5 }}>
            /api/v1/
          </Ltr>
          قرار دارند. پایهٔ نشانی را در پیکربندی برنامه‌تان نگه دارید، نه در
          خط‌به‌خط کد؛ روزی که محیط عوض شود، همین یک مقدار باید عوض شود.
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="محیط‌ها">
            <TableHead>
              <TableRow>
                <TableCell>محیط</TableCell>
                <TableCell>پایهٔ نشانی</TableCell>
                <TableCell>وضعیت</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ENVIRONMENTS.map((environment) => (
                <TableRow key={environment.baseUrl} hover>
                  <TableCell>{environment.labelFa}</TableCell>
                  <TableCell>
                    <Ltr component="code"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {environment.baseUrl}
                    </Ltr>
                  </TableCell>
                  <TableCell>{environment.availabilityFa}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section id="section-7" title="۷. کیف پول پیش‌پرداخت">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          پرداخت پیش از مصرف است. پیش از هر فراخوانی، بهای آن از موجودی قابل
          استفادهٔ شما نگه داشته می‌شود؛ اگر فراخوانی به پاسخ برسد، همان مبلغ
          برداشت می‌شود و اگر به پاسخ نرسد، مبلغ نگه‌داشته آزاد می‌شود.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          پاسخ منفی، پاسخ است: سرویسی که می‌گوید «تطبیق ندارد» کار خود را انجام
          داده و بهای آن برداشت می‌شود. موجودی و گردش مالی در{' '}
          <RouterLink to="/wallet">کیف پول و مالی</RouterLink> دیده می‌شود.
        </Typography>
      </Section>

      <Section id="section-8" title="۸. کلید یکتایی و شناسه‌ها">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هر فراخوانی که هزینه دارد یک کلید یکتایی می‌گیرد. اگر همان کلید با همان
          محتوا دوباره فرستاده شود، پاسخ پیشین بازپخش می‌شود و بار دوم هزینه‌ای
          برداشت نمی‌شود. همان کلید با محتوای متفاوت رد می‌شود.
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          کلید را از واحد کاری خودتان بسازید — شمارهٔ سفارش یا شناسهٔ کار — تا
          پس از راه‌اندازی دوبارهٔ فرایند هم همان بماند. کلیدی که با هر بار اجرا
          عوض می‌شود از پرداخت دوباره جلوگیری نمی‌کند.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          شناسهٔ همبستگی کار دیگری می‌کند: آن را خودتان می‌فرستید تا فراخوانی به
          گزارش‌های شما وصل شود. سامانه شناسهٔ درخواست خودش را می‌سازد و بازمی‌گرداند؛
          همان شناسه است که باید هنگام تماس با پشتیبانی اعلام کنید، چون روی
          درخواست، سند مالی و تلاش‌های اجرا نشسته است.
        </Typography>
      </Section>

      <Section id="section-9" title="۹. خطاهای عمومی">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هر شکست یک کد ماشین‌خوان دارد. کدها مستقل از سرویس‌دهنده‌اند و تنها به
          دو پرسش پاسخ می‌دهند: دوباره تلاش کنم؟ و آیا هزینه‌ای برداشت شد؟
        </Typography>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size="small" aria-label="خطاهای عمومی">
            <TableHead>
              <TableRow>
                <TableCell>کد</TableCell>
                <TableCell>معنی</TableCell>
                <TableCell>چه کنید</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {ERRORS.map((entry) => (
                <TableRow key={entry.code} hover>
                  <TableCell>
                    <Ltr component="code"
                      sx={{ fontFamily: 'monospace' }}
                    >
                      {entry.code}
                    </Ltr>
                  </TableCell>
                  <TableCell>{entry.meaning}</TableCell>
                  <TableCell>{entry.action}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Section>

      <Section id="section-10" title="۱۰. تاریخچهٔ درخواست‌ها">
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          هر فراخوانی در{' '}
          <RouterLink to="/requests">مصرف و درخواست‌ها</RouterLink> ثبت می‌شود، با
          شناسهٔ پیگیری، وضعیت، بهای اعلام‌شده، مبلغ کسرشده، بازگشت احتمالی و اثر
          نهایی روی موجودی. برای تطبیق با گردش مالی، همان شناسهٔ پیگیری در سطرهای{' '}
          <RouterLink to="/wallet">کیف پول</RouterLink> هم می‌آید.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          محتوای درخواست و پاسخ نگهداری نمی‌شود. این سامانه کد ملی، شمارهٔ کارت و
          شمارهٔ حساب را از خود عبور می‌دهد و ذخیره نمی‌کند؛ بنابراین «چه چیزی
          فرستادم» پرسشی است که ما نمی‌توانیم پاسخ دهیم و گزارش سمت خودتان باید
          آن را نگه دارد.
        </Typography>
      </Section>

      <Section id="section-11" title="۱۱. کتابخانهٔ Python">
        <Stack spacing={3}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              کتابخانهٔ رسمی، هم همگام و هم ناهمگام، با نوع‌های مشخص. تا پیش از
              انتشار عمومی، بستهٔ نصب را از ما بگیرید.
            </Typography>
            <CodeBlock title="نصب" code={INSTALL} />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              ساخت کارخواه و یک فراخوانی همگام. مقادیر زیر ساختگی‌اند.
            </Typography>
            <CodeBlock title="نمونهٔ همگام" code={SYNC_EXAMPLE} />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              همان سطح دسترسی، به‌صورت ناهمگام.
            </Typography>
            <CodeBlock title="نمونهٔ ناهمگام" code={ASYNC_EXAMPLE} />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              هر شکست یک استثنای مشخص است؛ لازم نیست رشتهٔ کد را خودتان مقایسه
              کنید.
            </Typography>
            <CodeBlock title="مدیریت خطا" code={ERROR_EXAMPLE} />
          </Box>

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              کلید یکتایی و شناسهٔ همبستگی، صریح و در کنار هم.
            </Typography>
            <CodeBlock title="کلید یکتایی و شناسهٔ همبستگی" code={IDEMPOTENCY_EXAMPLE} />
          </Box>
        </Stack>
      </Section>
    </Box>
  )
}

function Section({
  id,
  title,
  children,
}: {
  id: string
  title: string
  children: ReactNode
}) {
  return (
    <Paper
      variant="outlined"
      component="section"
      id={id}
      aria-labelledby={`${id}-heading`}
      // The app bar is fixed, so an anchor that scrolls a heading to the top
      // of the viewport puts it underneath the bar. Scroll margin is the one
      // line that fixes every anchor on the page at once.
      sx={{ p: 3, mb: 3, scrollMarginTop: 88 }}
    >
      <Typography variant="h2" id={`${id}-heading`} gutterBottom>
        {title}
      </Typography>
      {children}
    </Paper>
  )
}

/**
 * Where to start, for somebody who did not arrive at the top.
 *
 * Eleven sections is a guide; eleven sections with no index is a wall. This
 * is deliberately a list of links rather than a sticky sidebar: the page is
 * already narrow enough to read comfortably, and a second column would take
 * the width the code samples need.
 */
function Contents({ sections }: { sections: { id: string; title: string }[] }) {
  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Typography variant="h2" gutterBottom>
        فهرست مطالب
      </Typography>
      <Box
        component="ol"
        aria-label="فهرست مطالب"
        sx={{
          m: 0,
          pr: 2,
          columns: { xs: 1, sm: 2, md: 3 },
          '& li': { mb: 0.5, breakInside: 'avoid' },
        }}
      >
        {sections.map((section) => (
          <li key={section.id}>
            <Link href={`#${section.id}`} variant="body2" underline="hover">
              {section.title.replace(/^[۰-۹]+\.\s*/, '')}
            </Link>
          </li>
        ))}
      </Box>
    </Paper>
  )
}
